'use strict';

import './component.scss';

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

function setupDialog(dialog) {
    // Handle keyboard interactions (Tab trapping + Escape close)
    dialog.addEventListener('keydown', (event) => {
        // Trap focus within the dialog
        if (event.key === 'Tab') {
            const focusableSelectors = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]';
            const focusableElements = Array.from(dialog.querySelectorAll(focusableSelectors)).filter((el) => el.offsetParent !== null);

            if (focusableElements.length === 0) {
                return;
            }

            const firstEl = focusableElements[0];
            const lastEl = focusableElements[focusableElements.length - 1];
            const activeEl = document.activeElement;

            if (event.shiftKey && activeEl === firstEl) {
                event.preventDefault();
                lastEl.focus();
            } else if (!event.shiftKey && activeEl === lastEl) {
                event.preventDefault();
                firstEl.focus();
            }
        }

        // Close dialog with Escape key
        if (event.key === 'Escape') {
            event.preventDefault();
            closeDialogWithAnimation(dialog);
        }
    });

    // Handle click events (backdrop, buttons, and internal content)
    dialog.addEventListener(
        'click',
        (event) => {
            const rect = dialog.getBoundingClientRect();

            const clickedOutside =
                event.clientX < rect.left ||
                event.clientX > rect.right ||
                event.clientY < rect.top ||
                event.clientY > rect.bottom;

            // Clicked on the backdrop → close the dialog
            if (clickedOutside) {
                event.stopImmediatePropagation();
                event.preventDefault();
                closeDialogWithAnimation(dialog);
                return;
            }

            // Clicked on a [data-dialog-close] element to close the dialog
            if (event.target.closest('[data-dialog-close]')) {
                event.stopImmediatePropagation();
                event.preventDefault();
                closeDialogWithAnimation(dialog);
                return;
            }

            // Determine if the click is on an interactive element
            const interactiveSelectors = 'button, a[href], input, select, textarea, [role="button"]';
            const clickedInteractive = event.target.closest(interactiveSelectors);

            if (!clickedInteractive) {
                // Clicked inside the dialog but not on an interactive element to prevent bubbling
                event.stopPropagation();
                event.preventDefault();
            } else {
                // Allow normal interaction inside the dialog, but stop bubbling to avoid background clicks
                event.stopPropagation();
            }
        },
        true // Use capture phase to intercept clicks before they reach underlying elements
    );

    // Remove visibility class after closing
    dialog.addEventListener('close', () => {
        dialog.classList.remove('visible');
    });
}

// Opens a dialog by ID, ensuring only one modal is active at a time.
function openDialogById(id) {
    // Close any existing open dialogs
    document.querySelectorAll('dialog[open]').forEach((dialog) => {
        dialog.close();
        dialog.classList.remove('visible');
    });

    const dialog = document.getElementById(id);

    if (!dialog) {
        return;
    }

    try {
        dialog.showModal();

        requestAnimationFrame(() => {
            dialog.classList.add('visible');
        });

        // Focus the first element after a short delay to ensure proper render timing
        setTimeout(() => {
            const firstFocusable = dialog.querySelector(FOCUSABLE_SELECTOR);

            if (firstFocusable) {
                firstFocusable.focus();
            }
        }, 50);
    } catch (e) {
        // Handle cases where the dialog was previously open or invalid state errors
        if (e.name === 'InvalidStateError') {
            dialog.close();
            dialog.showModal();

            requestAnimationFrame(() => {
                dialog.classList.add('visible');
            });
        } else {
            console.error('Error opening dialog:', e);
        }
    }
}

// Closes a dialog with an exit transition before calling the .close() method.
function closeDialogWithAnimation(dialog) {
    if (!dialog || !dialog.open) {
        return;
    }

    dialog.classList.remove('visible');

    const computedStyle = window.getComputedStyle(dialog);
    const transitionDuration = parseFloat(computedStyle.transitionDuration) || 0;
    const transitionDelay = parseFloat(computedStyle.transitionDelay) || 0;
    const totalTransitionTime = (transitionDuration + transitionDelay) * 1000;

    // If transitions are disabled, for example by prefers-reduced-motion,
    // transitionend will not fire, so close the dialog immediately.
    if (totalTransitionTime === 0) {
        dialog.close();
        return;
    }

    let isClosed = false;

    const close = () => {
        if (isClosed) {
            return;
        }

        isClosed = true;
        dialog.close();
        dialog.removeEventListener('transitionend', handleTransitionEnd);
    };

    const handleTransitionEnd = (event) => {
        if (event.target === dialog) {
            close();
        }
    };

    dialog.addEventListener('transitionend', handleTransitionEnd);

    // Fallback in case transitionend does not fire.
    setTimeout(close, totalTransitionTime + 50);
}

// Initializes all dialog elements and their associated triggers on the page.
function init() {
    // Setup all dialogs
    document.querySelectorAll('dialog').forEach((dialog) => {
        setupDialog(dialog);

        // Auto-open support
        if (dialog.dataset.dialogAutoOpen === 'true') {
            try {
                dialog.showModal();

                requestAnimationFrame(() => {
                    dialog.classList.add('visible');
                });

                setTimeout(() => {
                    const firstFocusable = dialog.querySelector(FOCUSABLE_SELECTOR);

                    if (firstFocusable) {
                        firstFocusable.focus();
                    }
                }, 50);
            } catch (e) {
                console.error('Error auto-opening dialog', e);
            }
        }
    });

    // Setup triggers
    document.querySelectorAll('.dialog-trigger[data-dialog-id]').forEach((btn) => {
        btn.addEventListener('click', () => openDialogById(btn.dataset.dialogId));
    });
}

window.addEventListener('DOMContentLoaded', init);